import {Injectable} from "@angular/core";
import {Actions} from "@ngrx/effects";
import {Action, select, Store} from "@ngrx/store";
import {exhaustMap, mergeMap, Observable, of, withLatestFrom} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {ILocalPageOutputDto} from "@shared/interfaces/orga-response.interface";
import {ILocalPagination} from "@shared/interfaces/pagination.interface";
import {
  FileDto,
  FilterDto,
  IssueContentType,
  IssueListDto,
  IssueTemplateListDto
} from "@server-models";
import {Router} from "@angular/router";
import {SharedImageService} from "@shared/services/shared-image.service";
import {SharedCameraService} from "@shared/services/shared-camera.service";

import {Photo} from "@capacitor/camera";
import {SharedModalLoaderService} from "@shared/services/shared-modal-loader.service";
import {TranslateService} from "@ngx-translate/core";
import {SharedIssueBaseService} from "@shared/components/issues/services/shared-issue-base.service";
import {SharedIssueBaseEffects} from "@shared/components/issues/store/shared-issue-base.effects";
import {SharedIssueBaseActions} from "@shared/components/issues/store/shared-issue-base.action-type";
import {SharedIssueBaseSelectors} from "@shared/components/issues/store/shared-issue-base.selectors-type";
import {TGetIssue, TIssuePreview} from "@shared/components/issues/store/shared-issue-base.actions";

import {SharedIssueService} from "@shared/components/issues/services/shared-issue.service";
import {FormGroup} from "@angular/forms";
import {TTemplatePreviewDto} from "@shared/components/issues/types/template-preview-dto.type";
import {TCurrentIssueDto} from "@shared/components/issues/types/issue-dto.type";
import {TSubmitIssueDto} from "@shared/components/issues/types/submit-issue-dto.type";
import {SharedCacheService} from "@shared/services/cache/shared-cache.service";
import {ILocalIssueDetailData} from "@shared/components/issues/interfaces/local-issue-detail-data.interface";
import {SharedIssueApiService} from "@shared/components/issues/services/shared-issue-api.service";
import {ISharedLocalBodyRequest} from "@shared/interfaces/shared-local-body-request.interface";

import {SharedLoginSelectors} from "@shared/components/login/store/shared-login.selector-type";

@Injectable({
  providedIn: 'root'
})
export abstract class SharedIssueApiBaseEffects extends SharedIssueBaseEffects {

  protected constructor(
    actions$: Actions,
    store: Store,
    protected _cameraService: SharedCameraService,
    protected _sharedModalLoaderService: SharedModalLoaderService,
    protected _translateService: TranslateService,
    protected _imageService: SharedImageService,
    protected _issuesBaseService: SharedIssueBaseService,
    protected _sharedIssueApiService: SharedIssueApiService,
    protected _issueService: SharedIssueService,
    protected _cacheService: SharedCacheService,
    protected _router: Router,
  ) {
    super(store, actions$);
  }

  /**
   * @name requestGetPaginated
   * @description
   * request to issue service the list of issues
   * @memberof SharedIssueApiBaseEffects
   * @param action
   * @protected
   * @returns {Observable<(ILocalPageOutputDto<IssueListDto[]> | any)>}
   */
  protected abstract requestGetPaginated(action: ISharedLocalBodyRequest): Observable<(ILocalPageOutputDto<IssueListDto[]> | any)>;

  /**
   * @name requestGetTemplate
   * @description
   * request to get list of template
   * @memberof SharedIssueApiBaseEffects
   * @protected
   * @returns {Observable<(ILocalPageOutputDto<IssueTemplateListDto[]> | any)>}
   */
  protected abstract requestGetTemplate(templateId: number,
                                        resourceId?: number,
                                        filters?: FilterDto[],
                                        pageNumber?: number): Observable<(ILocalPageOutputDto<IssueTemplateListDto[]> | any)>;

  protected abstract requestIssuePreviewByContentType(action: TIssuePreview): Observable<(unknown | any)>;

  protected abstract requestByIdByIssueType(action: TGetIssue): Observable<TCurrentIssueDto>;

  protected abstract sendTemplatePreviewByIssueType(action: {
    templateId: number,
    issuePrepared: TSubmitIssueDto,
    issueContentType: IssueContentType
  }): Observable<any>;

  /**
   * @name savePictureRequest
   * @description
   * make request throw file api service to upload picture
   * @memberof SharedIssueApiBaseEffects
   * @param tenantId
   * @param blob
   * @param fileType
   * @param pictureId
   * @protected
   */
  protected abstract savePictureRequest(tenantId: number, blob: Blob, fileType: string, pictureId: number): Observable<{
    image: FileDto
  } | any>;

  /**
   * @name saveSignatureRequest
   * @description
   * make request throw file api service to upload signature
   * @memberof SharedIssueApiBaseEffects
   * @param tenantId
   * @param blob
   * @param dataUrl
   * @param fileType
   * @param index
   * @protected
   */
  protected abstract saveSignatureRequest(tenantId: number, blob: Blob, dataUrl: string, fileType: string,
                                          index: number | string): Observable<{
    signature: FileDto
  } | any>;

  protected _isLastPagePaginationState(action: ISharedLocalBodyRequest, state: ILocalPagination): boolean {
    return !state || action.pagination.pageNumber <= state.totalPages
  }

  getItemsPaginated(action$: Observable<ISharedLocalBodyRequest>): Observable<(ILocalPageOutputDto<IssueListDto[]> | any)> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(SharedIssueBaseSelectors.selectIssueListPaging))),
      mergeMap(([action, paginationState]) => {

          if (action.isRefresh) {
            return this.requestGetPaginated(action);
          } else if (this._isLastPagePaginationState(action, paginationState)) {
            return this.requestGetPaginated(action);
          } else {
            return [SharedIssueBaseActions.getIssueListPaginatedCancel()];
          }
        }
      )
    );
  }

  getItemsTemplate(action$: Observable<{
    templateId?: number;
    resourceId?: number;
    filters?: FilterDto[];
    pageNumber?: number;
  }>): Observable<(ILocalPageOutputDto<IssueTemplateListDto[]> | any)> {
    return action$.pipe(
      mergeMap(({ templateId, resourceId, pageNumber, filters }) =>
        this.requestGetTemplate(templateId!, resourceId, filters, pageNumber))
    );
  }

  getIssueTemplateListSuccess(action$: Observable<{
    data: ILocalPageOutputDto<IssueTemplateListDto[]>;
    templateId: number;
    resourceId?: number
  }>): Observable<any> {
    return action$.pipe(
      mergeMap(() => [SharedIssueBaseActions.getIssueTemplateListDone()])
    );
  }

  getIssuePreview(action$: Observable<ReturnType<typeof SharedIssueBaseActions.getIssuePreview>>): Observable<any> {
    return action$.pipe(
      mergeMap(action => this.requestIssuePreviewByContentType({
        ...action,
        issueType: action.contentType
      }))
    );
  }

  getIssuePreviewSuccess(action$: Observable<{
    data: TTemplatePreviewDto;
    resourceId?: number
  }>): Observable<(unknown | any)> {
    return of();
  }

  getByIdSuccess(action$: Observable<{
    data: TCurrentIssueDto
  }>): Observable<Action<string>> {
    return action$.pipe(
      mergeMap(({ data }) => {
        return [SharedIssueBaseActions.formatReadDetailData()]
      })
    )
  }

  formatReadDetailData(action$: Observable<Action<string>>): Observable<{
    detailDataFormatted: ILocalIssueDetailData
  }> {
    return action$.pipe(
      withLatestFrom(
        this.store.select(SharedIssueBaseSelectors.selectCurrentIssue),
        this.store.select(SharedIssueBaseSelectors.selectCurrentIssueType)
      ),
      exhaustMap(([_, issueDto, issueType]) => {
        const service = this._issueService.getIssueFormService(issueType);
        const detailDataFormatted = service.formatReadDetailData(issueDto, issueType);
        return [SharedIssueBaseActions.formatReadDetailDataFinish({ detailDataFormatted })]
      })
    )
  }

  formatReadDetailDataFinish(action$: Observable<{
    detailDataFormatted: ILocalIssueDetailData
  }>): Observable<any> {
    return action$;
  }

  prepareFormsToSendTemplatePreview(action$: Observable<{
    form: FormGroup,
    templatePreviewDto: any
  }>): Observable<any> {
    return action$.pipe(
      withLatestFrom(this.store.pipe((select(SharedIssueBaseSelectors.selectCurrentPreviewIssueType)))),
      mergeMap(([{ form, templatePreviewDto }, contentType]) => {
        const service = this._issueService.getIssueFormService(contentType);
        const submitReady = service.prepareFormIssueDto(form, templatePreviewDto);
        return [SharedIssueBaseActions.prepareFormsToSendTemplatePreviewDone({ submitReady })]
      })
    );
  }

  prepareFormsToSendTemplatePreviewDone(action$: Observable<{ submitReady: TSubmitIssueDto }>): Observable<any> {
    return action$.pipe(
      mergeMap(({ submitReady }) => {
        return [SharedIssueBaseActions.sendTemplatePreview({ issuePrepared: submitReady })]
      })
    );
  }

  sendTemplatePreview(action$: Observable<{ issuePrepared: TSubmitIssueDto }>): Observable<void> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(SharedIssueBaseSelectors.selectCurrentPreviewId))),
      withLatestFrom(this.store.pipe((select(SharedIssueBaseSelectors.selectCurrentPreviewIssueType)))),
      mergeMap(([[{ issuePrepared }, templateId], contentType]) => {
        return this.sendTemplatePreviewByIssueType({
          templateId,
          issuePrepared,
          issueContentType: contentType
        });
      })
    );
  }

  getIssueByIssueType(action$: Observable<any>): Observable<any> {
    return action$.pipe(
      mergeMap((action) => this.requestByIdByIssueType(action))
    );
  }

  askPictureInput(action$: Observable<{ isLoadingId: number, pictureId: number }>): Observable<{
    picture: Photo
  } | any> {
    return action$.pipe(
      exhaustMap((action) => {
        return this._cameraService.getPhoto().pipe(
          mergeMap((photo) => {
            return [SharedIssueBaseActions.askPictureInputSuccess({ picture: photo!, pictureId: action.pictureId })]
          }),
          catchError((error) => [SharedIssueBaseActions.askPictureInputFail({ error })])
        )

      })
    );
  }

  askPictureInputSuccess(action$: Observable<{ picture: Photo, pictureId: number }>): Observable<{
    picture: Photo
  } | any> {
    return action$.pipe(
      exhaustMap((data) => {
        return [SharedIssueBaseActions.reducePicture({ picture: data.picture, pictureId: data.pictureId })]
      })
    )
  }

  reducePicture(action$: Observable<{ picture: Photo, pictureId: number }>): Observable<{ blob: Blob } | any> {
    return action$.pipe(
      mergeMap((data) => {
        return this._imageService.resizeBlobImageObservable(data.picture)!.pipe(
          mergeMap((newData) => {
            return [SharedIssueBaseActions.reducePictureSuccess({ blob: newData, pictureId: data.pictureId })]
          }),
          catchError((error) => [SharedIssueBaseActions.reducePictureError({ error })])
        )
      })
    )
  }

  reducePictureSuccess(action$: Observable<{ blob: Blob, pictureId: number }>): Observable<{
    blob: Blob,
    fileType: string
  }> {
    return action$.pipe(
      exhaustMap((data) => {
        return [SharedIssueBaseActions.savePicture({
          blob: data.blob,
          fileType: data.blob.type,
          pictureId: data.pictureId
        })]
      })
    )
  }

  savePicture(action$: Observable<{ blob: Blob, fileType: string, pictureId: number }>): Observable<{
    picture: FileDto
  } | any> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(SharedIssueBaseSelectors.selectSelectedPictureAsBlob))),
      withLatestFrom(this.store.pipe(select(SharedLoginSelectors.selectTenant), map(tenant => tenant?.tenantId))),
      exhaustMap(([[action, selectedBlob], tenantId]) => {
        return this.savePictureRequest(tenantId!, selectedBlob, selectedBlob.type, action.pictureId)
      })
    );
  }

  convertDateURLToBlob(action$: Observable<{ isLoadingId: number; dataUrl: string }>): Observable<any> {
    return action$.pipe(
      mergeMap((data) => {
        return this._imageService.convertDataURLToBlobObservable(data.dataUrl).pipe(
          mergeMap((blob) => {
            return [SharedIssueBaseActions.convertDateURLToBlobSuccess({
              blob: blob!,
              dataUrl: data.dataUrl,
              index: data.isLoadingId
            })]
          }),
          catchError((error) => [SharedIssueBaseActions.convertDateURLToBlobFail({ error })])
        )
      })
    );
  }

  convertDateURLToBlobSuccess(action$: Observable<{ blob: Blob; dataUrl: string; index: number }>): Observable<any> {
    return action$.pipe(
      mergeMap((data) => [SharedIssueBaseActions.saveSignature({
        blob: data.blob,
        dataUrl: data.dataUrl,
        index: data.index
      })])
    )
  }

  saveSignature(action$: Observable<{ blob: Blob; dataUrl: string; index: number }>): Observable<{
    image: FileDto
  } | any> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(SharedIssueBaseSelectors.selectSelectedSignatureAsBlob))),
      withLatestFrom(this.store.pipe(select(SharedLoginSelectors.selectTenant), map(tenant => tenant?.tenantId))),
      mergeMap(([[action, selectedBlob], tenantId]) => {
        return this.saveSignatureRequest(tenantId!, selectedBlob, action.dataUrl, selectedBlob.type, action.index)
      })
    );
  }
}
